import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivationFormDialogComponent,
  DisplayActivationDialogComponent,
  ManualActivation,
  DeliveryMode,
} from '@lib/activation';
import { PERMISSION } from '@lib/core/enum';
import { HasPermissionDirective, PermissionService } from '@lib/core/permission';
import { LabelDisplayItemComponent } from '@lib/label';
import { AttributeComponent } from '@lib/ui';
import { DialogService } from '@proget-shared/dialog';
import { TableModule } from '@proget-shared/grid/table';
import { ResponseErrorTemplateMapper } from '@proget-shared/helper/response-error';
import { RoutingModule, RoutingTabItem } from '@proget-shared/routing';
import { TranslateModule } from '@proget-shared/translate';
import { EllipsisModule } from '@proget-shared/ui/ellipsis';
import { SpinnerModule } from '@proget-shared/ui/spinner';
import { ToastService } from '@proget-shared/ui/toast';
import { TooltipModule } from '@proget-shared/ui/tooltip';
import { catchError, EMPTY, noop, of, switchMap, throwError } from 'rxjs';

import { UserCardActionsComponent } from '../../component/actions/user-card-actions.component';
import { LimitedElementsComponent } from '../../component/limited-items/limited-items.component';
import { TABS } from '../../const';
import { Attribute, User } from '../../model';
import { UserCardService } from '../../service/user-card.service';
import { UserCardRepository } from '../../user-card.repository';

@Component({
  selector: 'lib-user-card-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  providers: [UserCardService],
  imports: [
    TooltipModule,
    EllipsisModule,
    SpinnerModule,
    HasPermissionDirective,
    AttributeComponent,
    TableModule,
    RoutingModule,
    TranslateModule,
    LabelDisplayItemComponent,
    LimitedElementsComponent,
    UserCardActionsComponent,
  ],
})
export class ViewComponent {
  protected readonly PERMISSION = PERMISSION;

  protected user: User;
  protected tabs: RoutingTabItem[];
  protected attributes: Attribute[] = [];
  protected requestPending = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(DialogService);
  private readonly userCardRepository = inject(UserCardRepository);
  private readonly toastService = inject(ToastService);
  private readonly userCardService = inject(UserCardService);

  private hasActiveActivation: boolean;

  constructor() {
    const permissionService = inject(PermissionService);

    this.userCardService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.user = user;
          this.hasActiveActivation = user.hasActiveActivation;
          this.tabs = TABS
            .filter((TAB) => permissionService.hasPermissionInstant(TAB.permission))
            .map((TAB): RoutingTabItem => ({
              title: TAB.title,
              route: TAB.route,
              permission: TAB.permission,
            }));
        },
      });

    this.userCardService.visibleAttributes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (attributes) => {
          this.attributes = attributes;
        },
      });
  }

  protected hasActivation(): boolean {
    return this.user && this.hasActiveActivation ;
  }

  protected generateActivation(): void {
    this.dialog
      .custom(ActivationFormDialogComponent)
      .pipe(
        catchError(() => EMPTY),
        switchMap((form) => this.userCardRepository.createActivation(
          this.user.uuid,
          form.activation.id,
          form.activation.deliveryMode === DeliveryMode.ASK ? form.sendEmail : form.activation.deliveryMode === DeliveryMode.SEND
        ))
      )
      .subscribe({
        next: (activation) => {
          this.hasActiveActivation = true;
          this.userCardService.reloadUser();
          this.displayActivationDialog(activation);
        },
        error: ({ form }) => {
          this.toastService.error(
            'library.user_card.toast.generate_activation.error',
            ResponseErrorTemplateMapper.mapStrings(form, true)
          );
        },
      });
  }

  protected displayActivation(): void {
    this.requestPending = true;
    this.userCardRepository
      .getActivation(this.user.uuid)
      .pipe(
        switchMap((activation) => (activation.active ? of(activation) : throwError(() => 'Activation inactive')))
      )
      .subscribe({
        next: (activation) => {
          this.displayActivationDialog(activation);
          this.requestPending = false;
        },
        error: () => {
          this.toastService.error('library.user_card.toast.display_activation.error');
          this.requestPending = false;
        },
      });
  }

  private displayActivationDialog(activation: ManualActivation): void {
    this.dialog
      .custom(DisplayActivationDialogComponent, {
        configuration: {
          activation,
        },
      })
      .subscribe({
        next: noop,
        error: () => {
          this.hasActiveActivation = false;
          this.toastService.error('library.user_card.toast.display_activation.error');
        },
      });
  }
}
